/**
 * Firm-id → logo path resolver.
 *
 * D1 firm ids are short slugs (e.g. `alphacapital`, `e8markets`) but the
 * logo files use display-style names (`alpha-capital-group.svg`,
 * `e8-markets.svg`) and varying extensions (`.svg`, `.png`, `.jpg`).
 *
 * This map is the single source of truth for that mapping. When a new
 * firm gets added to D1, append an entry here so its logo renders. When
 * the id is unknown, callers should fall back to a letter avatar.
 */

const FIRM_LOGO_MAP: Record<string, string> = {
  alphacapital: "alpha-capital-group.svg",
  alphafutures: "alpha-futures.svg",
  apextraderfunding: "apex-trader-funding.svg",
  aquafunded: "aquafunded.svg",
  atfunded: "atfunded.jpg",
  atmosfunded: "atmosfunded.svg",
  audacitycapital: "audacity-capital.svg",
  bemfunding: "bemfunding.svg",
  blueberry: "blueberry.png",
  blueguardian: "blueguardian.png",
  brightfunded: "brightfunded.svg",
  citytradersimperium: "citytradersimperium.svg",
  cryptofundtrader: "cryptofundtrader.svg",
  e8markets: "e8-markets.svg",
  equityedge: "equityedge.svg",
  finotive: "finotive.svg",
  fintokei: "fintokei.svg",
  fortraders: "for-traders.svg",
  ftmo: "ftmo.svg",
  fundedelite: "fundedelite.svg",
  fundednext: "fundednext.svg",
  fundedtradingplus: "funded-trading-plus.png",
  fundingpips: "funding-pips.svg",
  goat: "goat.svg",
  hantectrader: "hantec-trader.jpg",
  instantfunding: "instantfunding.svg",
  larkfunding: "lark-funding.svg",
  leveraged: "leveraged.svg",
  lucidtrading: "lucid-trading.jpg",
  maven: "maven.png",
  monetafunded: "monetafunded.png",
  myfundedfutures: "my-funded-futures.svg",
  qtfunded: "qtfunded.png",
  the5ers: "the5ers.svg",
  thinkcapital: "thinkcapital.png",
  toponetrader: "top-one-trader.svg",
  topstep: "topstep.png",
  tradethepool: "trade-the-pool.svg",
  tradeify: "tradeify.svg",
  // ─── Turkey-eligible futures firms (migration 0046+0047) ─────
  // Each futures firm carries its own logo file even when the parent
  // brand has an FX entry — the futures-side branding (e.g. Blueberry
  // Futures' red mark vs Blueberry Funded's blue) is intentionally
  // distinct. Sourced from PFM media CDN on 2026-04-26.
  fundednextfutures: "fundednext-futures.svg",
  traderslaunch: "traders-launch.svg",
  earn2trade: "earn2trade.svg",
  thetradingpitfutures: "the-trading-pit-futures.svg",
  tradeday: "tradeday.svg",
  fundedfuturesfamily: "funded-futures-family.svg",
  futureselite: "futureselite.svg",
  goatfundedfutures: "goat-funded-futures.svg",
  blueberryfutures: "blueberry-futures.svg",
};

/**
 * Resolve a firm id to its logo URL.
 * Returns null when the firm has no logo file — caller should render a
 * letter-avatar fallback (FirmLogo / FirmAvatar handle that).
 */
export function firmLogoUrl(firmId: string): string | null {
  const file = FIRM_LOGO_MAP[firmId];
  if (!file) return null;
  return `/logos/firms/${file}`;
}
